import React from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Save, Eye, FileText, List, Plus, Search, ChevronRight, AlignLeft } from 'lucide-react';
export function ContractTemplateBuilderPage() {
  const {
    templateId
  } = useParams();
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="h-[calc(100vh-8rem)] flex flex-col" data-id="element-2684">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 shrink-0" data-id="element-2685">
        <div data-id="element-2686">
          <div className="flex items-center gap-2 text-sm text-textMuted mb-1" data-id="element-2687">
            <span data-id="element-2688">Templates</span>
            <ChevronRight className="w-3 h-3" data-id="element-2689" />
            <span data-id="element-2690">Standard Performance Contract</span>
          </div>
          <h2 className="text-2xl font-bold" data-id="element-2691">
            Standard Performance Contract v3.2
          </h2>
        </div>
        <div className="flex items-center gap-3" data-id="element-2692">
          <button className="px-4 py-2 bg-surface border border-border rounded-lg hover:bg-surfaceHover transition-colors text-sm font-medium flex items-center gap-2" data-id="element-2693">
            <Eye className="w-4 h-4" data-id="element-2694" /> Preview
          </button>
          <button className="px-4 py-2 bg-primary text-background rounded-lg hover:bg-primaryHover transition-colors text-sm font-medium flex items-center gap-2" data-id="element-2695">
            <Save className="w-4 h-4" data-id="element-2696" /> Save Template
          </button>
        </div>
      </div>

      {/* Three-pane Builder */}
      <div className="flex-1 flex gap-6 min-h-0" data-id="element-2697">
        {/* Left Pane: Fields & Variables */}
        <div className="w-64 shrink-0 flex flex-col bg-surface border border-border rounded-xl overflow-hidden" data-id="element-2698">
          <div className="p-4 border-b border-border font-semibold flex items-center gap-2" data-id="element-2699">
            <List className="w-4 h-4" data-id="element-2700" /> Variables
          </div>
          <div className="p-4 border-b border-border" data-id="element-2701">
            <div className="relative" data-id="element-2702">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textMuted" data-id="element-2703" />
              <input type="text" placeholder="Search variables..." className="w-full pl-9 pr-4 py-1.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary" data-id="element-2704" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2705">
            <div data-id="element-2706">
              <h4 className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-2" data-id="element-2707">
                Event Details
              </h4>
              <div className="space-y-2" data-id="element-2708">
                <button className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg text-sm hover:border-primary transition-colors flex items-center justify-between group" data-id="element-2709">
                  <span data-id="element-2710">{`{{EventDate}}`}</span>
                  <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-primary" data-id="element-2711" />
                </button>
                <button className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg text-sm hover:border-primary transition-colors flex items-center justify-between group" data-id="element-2712">
                  <span data-id="element-2713">{`{{VenueName}}`}</span>
                  <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-primary" data-id="element-2714" />
                </button>
                <button className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg text-sm hover:border-primary transition-colors flex items-center justify-between group" data-id="element-2715">
                  <span data-id="element-2716">{`{{VenueAddress}}`}</span>
                  <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-primary" data-id="element-2717" />
                </button>
              </div>
            </div>
            <div data-id="element-2718">
              <h4 className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-2" data-id="element-2719">
                Financials
              </h4>
              <div className="space-y-2" data-id="element-2720">
                <button className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg text-sm hover:border-primary transition-colors flex items-center justify-between group" data-id="element-2721">
                  <span data-id="element-2722">{`{{GuaranteeFee}}`}</span>
                  <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-primary" data-id="element-2723" />
                </button>
                <button className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg text-sm hover:border-primary transition-colors flex items-center justify-between group" data-id="element-2724">
                  <span data-id="element-2725">{`{{DepositAmount}}`}</span>
                  <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-primary" data-id="element-2726" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Center Pane: Canvas */}
        <div className="flex-1 flex flex-col bg-surface border border-border rounded-xl overflow-hidden" data-id="element-2727">
          <div className="p-2 border-b border-border flex items-center gap-2 bg-background/50" data-id="element-2728">
            <button className="p-1.5 hover:bg-surface rounded text-textMuted hover:text-textMain" data-id="element-2729">
              <AlignLeft className="w-4 h-4" data-id="element-2730" />
            </button>
            <div className="w-px h-4 bg-border mx-1" data-id="element-2731"></div>
            <select className="bg-transparent text-sm focus:outline-none text-textMuted" data-id="element-2732">
              <option data-id="element-2733">Normal Text</option>
              <option data-id="element-2734">Heading 1</option>
              <option data-id="element-2735">Heading 2</option>
            </select>
          </div>
          <div className="flex-1 p-8 overflow-y-auto bg-background/30" data-id="element-2736">
            <div className="max-w-2xl mx-auto bg-surface border border-border rounded shadow-sm min-h-full p-8 font-serif text-sm leading-relaxed" data-id="element-2737">
              <h1 className="text-xl font-bold text-center mb-8" data-id="element-2738">
                PERFORMANCE AGREEMENT
              </h1>
              <p className="mb-4" data-id="element-2739">
                This Performance Agreement (the "Agreement") is entered into as
                of {`{{CurrentDate}}`}, by and between:
              </p>
              <p className="mb-4" data-id="element-2740">
                <strong data-id="element-2741">ARTIST:</strong> {`{{ArtistName}}`}
                <br data-id="element-2742" />
                <strong data-id="element-2743">VENUE/PROMOTER:</strong> {`{{VenueName}}`}
              </p>
              <h2 className="font-bold mt-6 mb-2" data-id="element-2744">1. ENGAGEMENT DETAILS</h2>
              <p className="mb-4" data-id="element-2745">
                The Venue hereby engages the Artist to perform at{' '}
                {`{{VenueName}}`} located at {`{{VenueAddress}}`} on{' '}
                {`{{EventDate}}`}.
              </p>
              <h2 className="font-bold mt-6 mb-2" data-id="element-2746">2. COMPENSATION</h2>
              <p className="mb-4" data-id="element-2747">
                Venue agrees to pay Artist a guaranteed fee of{' '}
                {`{{GuaranteeFee}}`}. A deposit of {`{{DepositAmount}}`} is due
                upon signing.
              </p>
              <div className="mt-12 border-2 border-dashed border-primary/30 bg-primary/5 p-4 rounded text-center text-primary text-sm" data-id="element-2748">
                Drop clause here
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane: Clause Library */}
        <div className="w-72 shrink-0 flex flex-col bg-surface border border-border rounded-xl overflow-hidden" data-id="element-2749">
          <div className="p-4 border-b border-border font-semibold flex items-center gap-2" data-id="element-2750">
            <FileText className="w-4 h-4" data-id="element-2751" /> Clause Library
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2752">
            <div data-id="element-2753">
              <h4 className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-2" data-id="element-2754">
                Force Majeure
              </h4>
              <div className="p-3 bg-background border border-border rounded-lg text-xs text-textMuted cursor-grab hover:border-primary transition-colors" data-id="element-2755">
                Standard "Act of God" cancellation terms protecting both
                parties.
              </div>
            </div>
            <div data-id="element-2756">
              <h4 className="text-xs font-semibold text-textMuted uppercase tracking-wider mb-2" data-id="element-2757">
                Radius Clause
              </h4>
              <div className="p-3 bg-background border border-border rounded-lg text-xs text-textMuted cursor-grab hover:border-primary transition-colors mb-2" data-id="element-2758">
                <strong data-id="element-2759">Strict (60 miles / 30 days)</strong>
                <br data-id="element-2760" />
                Artist shall not perform within 60 miles...
              </div>
              <div className="p-3 bg-background border border-border rounded-lg text-xs text-textMuted cursor-grab hover:border-primary transition-colors" data-id="element-2761">
                <strong data-id="element-2762">Lenient (30 miles / 14 days)</strong>
                <br data-id="element-2763" />
                Artist shall not perform within 30 miles...
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}